import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import RtlLayout from "layouts/rtl";
import AdminLayout from "layouts/admin";
import AuthLayout from "layouts/auth";
import AdminApp from "admin/AdminApp";
import PartnerApp from "partner/PartnerApp";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "context/globalContext";
import { /* ROUTES, */ getDefaultRouteForHost, hostPortalRedirect } from "constants";
import { isValidJWT } from "helpers/isValidJWT";
import ResetPasswordPage from "components/resetPasswordPage/ResetPasswordPage";
import { decryptData } from "helpers";

const App = () => {
  const navigate = useNavigate();
  const { setUserDetails } = useGlobalContext();
  const location = useLocation();

  const { pathname } = location;

  useEffect(() => {
    // /admin and /partnerAdmin run their own self-contained auth. The legacy
    // jwt check below must never bounce them — a stale `jwtToken` left in
    // localStorage by the legacy pages sent every route change inside the
    // portals (e.g. opening a booking) to /auth/login.
    if (pathname.startsWith("/admin") || pathname.startsWith("/partnerAdmin")) return;
    const token = localStorage.getItem("jwtToken");
    if (token && isValidJWT(token)) {
      try {
        const decodedToken = jwtDecode(decryptData(token));
        setUserDetails(decodedToken);
        if (!decodedToken?.id) {
          navigate("/auth/login");
          localStorage.removeItem("jwtToken");
        }
      } catch (error) {
        console.error("Invalid token provided", error);
      }
    } else {
      localStorage.removeItem("jwtToken");
      if (pathname.startsWith("/resetpassword")) return;
      // "/" is about to be redirected by the Route below to whichever app
      // matches the current host — don't race it with a legacy redirect.
      // /admin and /partnerAdmin run their own
      // self-contained auth and must not be bounced by this legacy jwt check.
      if (
        pathname === "/" ||
        pathname.startsWith("/admin") ||
        pathname.startsWith("/partnerAdmin")
      )
        return;
      navigate("/auth/login");
    }
  }, [navigate, setUserDetails, pathname]);

  /**
   * A host serves ONE portal, and only when enforcement is switched on.
   *
   * `admin-pat.zenvoya.ai/admin/login` is the CRM; typing `/partnerAdmin/login`
   * onto it used to render the partner console just as happily, because the
   * host only ever decided where `/` redirected to. This turns that into a
   * redirect back to the host's own portal.
   *
   * Placed AHEAD of the route table rather than on each `<Route element>`: the
   * rule covers five prefixes across three portals, and one gate is what keeps
   * a newly added route from being fenced on three screens and forgotten on the
   * fourth. Every hook above still runs first, so hook order is unaffected.
   *
   * Returns null — and renders nothing different — whenever enforcement is off,
   * the host names no portal (localhost, preview builds), or the path belongs to
   * no portal at all. See `PORTAL_HOST_ENFORCED` for why the default is off and
   * what has to be true in an environment before it can be turned on.
   */
  const wrongPortalForHost = hostPortalRedirect(pathname);
  if (wrongPortalForHost) return <Navigate to={wrongPortalForHost} replace />;

  return (
    <Routes>
      <Route path="/resetpassword/:id" element={<ResetPasswordPage />} />
      <Route path="auth/*" element={<AuthLayout />} />
      <Route path="legacyAdmin/*" element={<AdminLayout />} />
      <Route path="admin/*" element={<AdminApp />} />
      <Route path="partnerAdmin/*" element={<PartnerApp />} />
      <Route path="rtl/*" element={<RtlLayout />} />
      <Route path="/" element={<Navigate to={getDefaultRouteForHost()} replace />} />
    </Routes>
  );
};

export default App;
